/**
 * Gemeinsame Helfer der drei Kalenderansichten -- #311.
 *
 * Bis Sprint 9 lagen diese Funktionen in doppelter bis dreifacher Ausfertigung
 * in CalendarView.js, GlobalCalendarView.js und DayCalendarPanel.js. Der
 * eigentliche Schaden war nie die Dateigroesse, sondern die Drift: ein Fix
 * landete jeweils nur in einer der Kopien.
 *
 * Regel fuer dieses Modul: KEINE React-Abhaengigkeit. Alles, was die Komponente
 * an Zustand beisteuert -- das Arbeitszeitfenster (timeStartHour/timeEndHour)
 * und die Sprache (locale) --, kommt als Parameter herein. Nur so sind die
 * Funktionen einzeln testbar.
 *
 * Wer nicht bei jedem Aufruf Zusatzargumente durchreichen will, nimmt
 * erstelleKalenderHelfer({ timeStartHour, timeEndHour, locale }) -- das liefert
 * dieselben Funktionen mit gebundenem Kontext, sodass die Aufrufstellen in den
 * Komponenten unveraendert bleiben.
 */

// ---------------------------------------------------------------- Zeitzone

export const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Graph liefert Zeiten ohne Zonen-Suffix, gemeint ist aber UTC. Ohne das 'Z'
 * legt der Browser seine eigene Zone unter -- im Sommer zwei Stunden daneben.
 *
 * Die tolerante Fassung (aus DayCalendarPanel) ist bewusst die gemeinsame:
 * bei einem fehlenden Wert entsteht ein "Invalid Date" statt eines Absturzes
 * mitten im Rendern des Kalenders.
 */
export const alsUtc = (dateTimeString) => (
  dateTimeString?.endsWith?.('Z') ? dateTimeString : `${dateTimeString}Z`
);

/** Stunde und Minute eines Zeitpunkts in der Zone des Benutzers. */
const wandUhrzeit = (datum, locale) => {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: getUserTimeZone(),
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  }).formatToParts(datum);
  return {
    hours: parseInt(parts.find(part => part.type === 'hour').value),
    minutes: parseInt(parts.find(part => part.type === 'minute').value)
  };
};

// ---------------------------------------------------------------- Formatierung

export const formatTime = (dateTimeString, locale) => (
  new Date(alsUtc(dateTimeString)).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getUserTimeZone()
  })
);

/**
 * Wie formatTime, aber OHNE UTC-Normierung: geplante Ticketzeiten kommen aus
 * der eigenen Datenbank und tragen ihre Zone bereits. Der Unterschied sieht
 * wie eine Unachtsamkeit aus, ist aber der Grund, warum es zwei Funktionen gibt.
 */
export const formatTicketTime = (dateTimeString, locale) => (
  new Date(dateTimeString).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getUserTimeZone()
  })
);

// ---------------------------------------------------------------- Zeitspannen

/** Dauer eines Graph-Termins in Minuten. */
export const getEventDuration = (event) => {
  const start = new Date(alsUtc(event.start.dateTime));
  const end = new Date(alsUtc(event.end.dateTime));
  return (end - start) / (1000 * 60);
};

/** Zeitspanne eines Kalender-Eintrags, egal ob Termin oder geplantes Ticket. */
export const getItemTimeRange = (item) => {
  if (item.type === 'event') {
    const event = item.data;
    return {
      start: new Date(alsUtc(event.start.dateTime)),
      end: new Date(alsUtc(event.end.dateTime))
    };
  }
  const ticket = item.data;
  return {
    start: new Date(ticket.plannedStartDateTime),
    end: new Date(ticket.plannedEndDateTime)
  };
};

export const timeRangesOverlap = (start1, end1, start2, end2) => start1 < end2 && start2 < end1;

/** Snapping auf 30-Minuten-Raster. */
export const snapToGrid = (minutes) => Math.round(minutes / 30) * 30;

/** Vergleicht zwei Zeitpunkte auf Tagesgleichheit in der Zone des Benutzers. */
export const istSelberTag = (a, b, locale) => {
  const format = new Intl.DateTimeFormat(locale, {
    timeZone: getUserTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return format.format(a) === format.format(b);
};

// ---------------------------------------------------------------- Termin-Metadaten

const lesReferenz = (eventBody, muster, was) => {
  try {
    if (!eventBody || !eventBody.content) return null;
    const match = eventBody.content.match(muster);
    return match ? match[1] : null;
  } catch (error) {
    console.error(`Error extracting ${was} from event:`, error);
    return null;
  }
};

/**
 * Die Body-Referenz ist das EINZIGE Kriterium fuer Ticket-zu-Termin (siehe
 * AGENTS.md, #191) -- Betreff und Standort sind es ausdruecklich nicht.
 */
export const extractTicketNumberFromEvent = (eventBody) => lesReferenz(eventBody, /\[TICKET:([^\]]+)\]/, 'ticket number');

export const extractSeriesIdFromEvent = (eventBody) => lesReferenz(eventBody, /\[SERIES:([^\]]+)\]/, 'series ID');

/** Trennt den Block-Zaehler "- (2)" vom eigentlichen Titel (#121). */
export const parseBlockSuffix = (title) => {
  try {
    const raw = String(title || '');
    const m = raw.match(/\s*\((\d+)\)\s*$/);
    if (m) {
      const num = parseInt(m[1], 10);
      const baseTitle = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
      return { baseTitle, existingBlock: (Number.isNaN(num) ? null : num) };
    }
    return { baseTitle: raw.trim(), existingBlock: null };
  } catch (_) {
    return { baseTitle: String(title || '').trim(), existingBlock: null };
  }
};

// ---------------------------------------------------------------- Darstellung

/**
 * Hinweis: nur .color wird gelesen -- und der ist fuer jeden Eingabewert
 * derselbe. Der Text ist heute ohne Abnehmer (und waere ausserdem nicht
 * uebersetzt). Steht bewusst unveraendert hier, damit diese Zusammenfuehrung
 * nichts nebenbei entscheidet.
 */
export const getEventStatus = (showAs) => {
  switch (showAs) {
    case 'busy':
      return { color: 'var(--color-cal-termin)', text: 'Beschäftigt' };
    case 'tentative':
      return { color: 'var(--color-cal-termin)', text: 'Vorläufig' };
    case 'free':
      return { color: 'var(--color-cal-termin)', text: 'Frei' };
    case 'oof':
      return { color: 'var(--color-cal-termin)', text: 'Abwesend' };
    default:
      return { color: 'var(--color-cal-termin)', text: 'Termin' };
  }
};

// #604: Der Block hat nur zwei Farben, Standard und „Gebucht“. Die Priorität
// steht als Punkt in der Kopfzeile (components/kalender/KalenderTicketKennung),
// bis #604 färbte sie den ganzen Block.
export const getTicketColor = (ticket) => {
  // Abgeschlossene Tickets werden immer grau dargestellt
  if (ticket?.status === 'abgeschlossen') return 'var(--color-cal-abgeschlossen)';
  if (ticket?.isGebucht) return 'var(--color-cal-gebucht)';
  return 'var(--color-cal-normal)';
};

// ---------------------------------------------------------------- Geometrie
// Alles hier haengt am Arbeitszeitfenster, das der Benutzer umstellen kann
// (8-18 als Standard, 0/1 bis 23/24 als Ausklappung) -- deshalb Parameter.

export const getTimeSlots = (timeStartHour, timeEndHour) => {
  const slots = [];
  for (let hour = timeStartHour; hour < timeEndHour; hour++) {
    slots.push(hour);
  }
  return slots;
};

const hoeheInProzent = (dauerMinuten, timeStartHour, timeEndHour) => {
  const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
  // minimum height of 2%, max 100%
  return Math.max(2, Math.min(100, (dauerMinuten / workingDayMinutes) * 100));
};

export const getEventHeight = (event, timeStartHour, timeEndHour) => (
  hoeheInProzent(getEventDuration(event), timeStartHour, timeEndHour)
);

export const getTicketHeight = (ticket, timeStartHour, timeEndHour) => {
  const start = new Date(ticket.plannedStartDateTime);
  const end = new Date(ticket.plannedEndDateTime);
  return hoeheInProzent((end - start) / (1000 * 60), timeStartHour, timeEndHour);
};

const positionInProzent = (datum, timeStartHour, timeEndHour, locale) => {
  const { hours, minutes } = wandUhrzeit(datum, locale);

  // Calculate position within dynamic hour range
  if (hours < timeStartHour) return 0;
  if (hours >= timeEndHour) return 100;

  const totalMinutes = (hours - timeStartHour) * 60 + minutes;
  const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
  return Math.max(0, Math.min(100, (totalMinutes / workingDayMinutes) * 100));
};

export const getEventPosition = (event, timeStartHour, timeEndHour, locale) => (
  positionInProzent(new Date(alsUtc(event.start.dateTime)), timeStartHour, timeEndHour, locale)
);

export const getTicketPosition = (ticket, timeStartHour, timeEndHour, locale) => (
  positionInProzent(new Date(ticket.plannedStartDateTime), timeStartHour, timeEndHour, locale)
);

/**
 * Position des Jetzt-Markers -- null bedeutet "ausserhalb der angezeigten
 * Stunden, Marker nicht zeichnen". Der Zeitpunkt kommt als Parameter, weil er
 * sich jede Minute aendert und sonst jede Memoisierung wertlos waere.
 */
export const getCurrentTimePosition = (now, timeStartHour, timeEndHour, locale) => {
  const { hours, minutes } = wandUhrzeit(now, locale);

  // Don't show marker outside working hours
  if (hours < timeStartHour || hours >= timeEndHour) return null;

  const totalMinutes = (hours - timeStartHour) * 60 + minutes;
  const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
  return Math.max(0, Math.min(100, (totalMinutes / workingDayMinutes) * 100));
};

// ---------------------------------------------------------------- Tagesauswahl

/** Anfang und Ende eines Kalendertages in der Ortszeit des Browsers. */
export const tagesGrenzen = (day) => {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);
  return { dayStart, dayEnd };
};

/**
 * Die Termine und geplanten Tickets eines Tages, als gemeinsame Liste.
 *
 * Diese Auswahl lag bis #308 in VIER Fassungen vor: `getEventsForDay`
 * (CalendarView), `getUserEventsForDay` (GlobalCalendarView), der useMemo in
 * DayCalendarPanel -- und, am teuersten, ein zweites Mal in der
 * Render-Schleife des globalen Kalenders, nur um die Pfeile "es gibt etwas
 * ausserhalb des Fensters" zu setzen.
 *
 * Ein Eintrag zaehlt zum Tag, wenn er darin beginnt, darin endet oder ihn
 * ueberspannt -- ein mehrtaegiger Termin erscheint also an jedem Tag.
 */
export const tagesEintraege = (events, tickets, dayStart, dayEnd) => {
  const imFenster = (start, ende) => (
    (start >= dayStart && start <= dayEnd) ||
    (ende >= dayStart && ende <= dayEnd) ||
    (start <= dayStart && ende >= dayEnd)
  );

  return [
    ...(events || [])
      .filter(event => imFenster(new Date(alsUtc(event.start.dateTime)), new Date(alsUtc(event.end.dateTime))))
      .map(data => ({ type: 'event', data })),
    ...(tickets || [])
      .filter(ticket => imFenster(new Date(ticket.plannedStartDateTime), new Date(ticket.plannedEndDateTime)))
      .map(data => ({ type: 'ticket', data }))
  ];
};

/**
 * Das Stundenformat fuer die Fenster-Pfeile. Bewusst als eigene Funktion:
 * der globale Kalender legte sich davon eins PRO ZELLE an -- bei 45 Zellen
 * gemessene 8,1 ms je Render, ein Viertel der Gesamtkosten (#308).
 */
export const erstelleStundenFormat = (locale) => new Intl.DateTimeFormat(locale, {
  timeZone: getUserTimeZone(),
  hour: 'numeric',
  hour12: false
});

/**
 * Liegt etwas ausserhalb des angezeigten Zeitfensters? Steuert die beiden
 * Pfeile ueber und unter einer Tagesspalte.
 *
 * Bekommt bewusst die UNGEFILTERTE Tagesliste: ein frueher Termin soll den
 * Pfeil auch dann zeigen, wenn er als Ticket-Dublette aus der Darstellung
 * faellt.
 */
export const ausserhalbFenster = (items, hourFmt, timeStartHour, timeEndHour) => {
  const stunde = (datum) => parseInt(hourFmt.format(datum));
  return {
    hasAbove: items.some(item => stunde(getItemTimeRange(item).start) < timeStartHour),
    hasBelow: items.some(item => stunde(getItemTimeRange(item).end) >= timeEndHour)
  };
};

// ---------------------------------------------------------------- Spaltensatz

/**
 * Verteilt ueberlappende Eintraege eines Tages auf Spalten.
 *
 * ACHTUNG, geaenderter Vertrag gegenueber den drei Alt-Fassungen: die Funktion
 * filtert NICHT mehr selbst. Vorher rief sie intern filterDuplicateGraphEvents
 * auf -- und genau diese Filterung ist in den drei Kalendern verschieden
 * (persoenlich: mit Serien-Kennung; global: ueber alle Bearbeiter; Tagesleiste:
 * ohne Gruppierung). Der Aufrufer filtert jetzt und uebergibt das Ergebnis.
 *
 * Nebeneffekt, auf den #308 aufbaut: dadurch ist das hier eine reine Funktion
 * ihrer Eingabe und laesst sich memoisieren.
 */
export const calculateItemPositions = (items) => {
  if (items.length === 0) return [];

  // Sort items by start time
  const sortedItems = [...items].sort((a, b) => getItemTimeRange(a).start - getItemTimeRange(b).start);

  // Calculate overlapping groups
  const overlappingGroups = [];
  for (const currentItem of sortedItems) {
    const currentTimeRange = getItemTimeRange(currentItem);

    // Find or create a group for this item
    const assignedGroup = overlappingGroups.find(group => group.some(groupItem => {
      const groupTimeRange = getItemTimeRange(groupItem);
      return timeRangesOverlap(
        currentTimeRange.start, currentTimeRange.end,
        groupTimeRange.start, groupTimeRange.end
      );
    }));

    if (assignedGroup) {
      assignedGroup.push(currentItem);
    } else {
      overlappingGroups.push([currentItem]);
    }
  }

  // Frisches Objekt je Eintrag -- die Alt-Fassungen legten hier ebenfalls
  // jedes Mal ein eigenes an, und ein gemeinsames waere von aussen veraenderbar.
  const volleBreite = () => ({ column: 0, totalColumns: 1, left: 2, width: 'calc(100% - 4px)' });

  // Calculate positions for each item
  const itemPositions = new Map();

  overlappingGroups.forEach(group => {
    if (group.length === 1) {
      // Single item, full width
      itemPositions.set(group[0], volleBreite());
      return;
    }

    // Multiple overlapping items, arrange in columns.
    // Sort group items by start time for consistent column assignment
    const sortedGroupItems = group.sort((a, b) => getItemTimeRange(a).start - getItemTimeRange(b).start);

    // Assign columns using a greedy algorithm
    const columnAssignments = [];
    const columnEndTimes = [];

    sortedGroupItems.forEach(item => {
      const itemTimeRange = getItemTimeRange(item);

      // Find the first available column
      let assignedColumn = 0;
      for (let col = 0; col < columnEndTimes.length; col++) {
        if (columnEndTimes[col] <= itemTimeRange.start) {
          assignedColumn = col;
          break;
        }
        assignedColumn = col + 1;
      }

      // Update or add column end time
      if (assignedColumn < columnEndTimes.length) {
        columnEndTimes[assignedColumn] = itemTimeRange.end;
      } else {
        columnEndTimes.push(itemTimeRange.end);
      }

      columnAssignments.push({ item, column: assignedColumn });
    });

    // Calculate the actual total columns needed
    const actualTotalColumns = Math.max(...columnAssignments.map(ca => ca.column)) + 1;

    // Set positions for each item
    columnAssignments.forEach(({ item, column }) => {
      const widthPercent = 100 / actualTotalColumns;
      itemPositions.set(item, {
        column,
        totalColumns: actualTotalColumns,
        left: `${column * widthPercent}%`,
        width: `${widthPercent - 1}%` // -1% for gap between items
      });
    });
  });

  return items.map(item => ({
    ...item,
    position: itemPositions.get(item) || volleBreite()
  }));
};

// ---------------------------------------------------------------- Bindung

/**
 * Bindet das Arbeitszeitfenster und die Sprache an die Helfer, damit die
 * Aufrufstellen in den Komponenten ohne Zusatzargumente auskommen.
 *
 * In der Komponente einmal pro Aenderung von timeStartHour/timeEndHour/locale
 * erzeugen (useMemo) und per Destrukturierung auspacken.
 */
export const erstelleKalenderHelfer = ({ timeStartHour, timeEndHour, locale }) => ({
  getUserTimeZone,
  formatTime: (dateTimeString) => formatTime(dateTimeString, locale),
  formatTicketTime: (dateTimeString) => formatTicketTime(dateTimeString, locale),
  getEventDuration,
  getItemTimeRange,
  timeRangesOverlap,
  snapToGrid,
  extractTicketNumberFromEvent,
  extractSeriesIdFromEvent,
  parseBlockSuffix,
  getEventStatus,
  getTicketColor,
  isToday: (date) => istSelberTag(new Date(), date, locale),
  getTimeSlots: () => getTimeSlots(timeStartHour, timeEndHour),
  getEventHeight: (event) => getEventHeight(event, timeStartHour, timeEndHour),
  getTicketHeight: (ticket) => getTicketHeight(ticket, timeStartHour, timeEndHour),
  getEventPosition: (event) => getEventPosition(event, timeStartHour, timeEndHour, locale),
  getTicketPosition: (ticket) => getTicketPosition(ticket, timeStartHour, timeEndHour, locale),
  getCurrentTimePosition: (now) => getCurrentTimePosition(now, timeStartHour, timeEndHour, locale),
  calculateItemPositions,
  tagesGrenzen,
  tagesEintraege,
  erstelleStundenFormat: () => erstelleStundenFormat(locale),
  ausserhalbFenster: (items, hourFmt) => ausserhalbFenster(items, hourFmt, timeStartHour, timeEndHour)
});
